import React, { ReactNode, useState } from 'react'

import { useField, useFormikContext } from 'formik'

import Popover from '../../dataDisplay/Popover'
import Progress from '../../feedback/Progress'
import { lowercaseRegx, numberRegx, specialCharRegx, uppercaseRegx } from '../../utils/Regx'
import Label from '../components/Label'
import PasswordInput from '../components/PasswordInput'
import PasswordPopoverContent, { ContentProps } from '../components/PasswordPopoverContent'
import FieldProps from './FieldProps'
import Error from './FormItemError'

import './FormikStyles.less'

interface PasswordProps extends FieldProps {
  size?: 'large' | 'default' | 'small'
  onPressEnter?: React.KeyboardEventHandler<HTMLInputElement>
  disabled?: boolean
  defaultValue?: string
  popover?: boolean
  setPassword: (value: string) => void
  progress?: boolean
  hint?: string
}

const TOTAL_REQUIRED_STEPS = 5
const TOTAL_PROGRESS = 100
const NR_OF_CHARS_REQUIRED = 8
const SINGLE_STEP_PROGRESS = TOTAL_PROGRESS / TOTAL_REQUIRED_STEPS
const hasNumberKey = 1
const hasUppercaseKey = 2
const hasLowerCaseKey = 3
const hasLength8Key = 4
const hasSpecialCharsKey = 5

let progressMap = new Map<number, boolean>([
  [hasNumberKey, false],
  [hasUppercaseKey, false],
  [hasLowerCaseKey, false],
  [hasLength8Key, false],
  [hasSpecialCharsKey, false],
])

const FormikInputField: React.FC<PasswordProps | any> = (props) => {
  const [field, meta] = useField(props)
  const showError = meta.touched && meta.error
  const [content, setContent] = useState<ReactNode>(
    <PasswordPopoverContent
      nrOfcharsIcon={false}
      lowercaseIcon={false}
      oneNumberIcon={false}
      specialCharIcon={false}
      uppercaseIcon={false}
    />,
  )
  const [progressState, setProgress] = useState<number>(0)

  const { setFieldValue } = useFormikContext()

  let passwordValidationResult: ContentProps = {
    nrOfcharsIcon: false,
    lowercaseIcon: false,
    oneNumberIcon: false,
    specialCharIcon: false,
    uppercaseIcon: false,
  }

  const calculatePasswordPopoverContent = (password: string) => {
    passwordValidationResult.oneNumberIcon = password.match(numberRegx) != null
    passwordValidationResult.lowercaseIcon = password.match(lowercaseRegx) != null
    passwordValidationResult.nrOfcharsIcon = password.length >= 8
    passwordValidationResult.specialCharIcon = password.match(specialCharRegx) != null
    passwordValidationResult.uppercaseIcon = password.match(uppercaseRegx) != null

    setContent(<PasswordPopoverContent {...passwordValidationResult} />)
  }

  const calculateProgress = (password: string) => {
    progressMap.set(hasNumberKey, password.match(numberRegx) != null)
    progressMap.set(hasLowerCaseKey, password.match(lowercaseRegx) != null)
    progressMap.set(hasUppercaseKey, password.match(uppercaseRegx) != null)
    progressMap.set(hasSpecialCharsKey, password.match(specialCharRegx) != null)
    progressMap.set(hasLength8Key, password.length >= NR_OF_CHARS_REQUIRED)

    let progressCount = 0
    progressMap.forEach((value, key) => {
      if (value) progressCount += SINGLE_STEP_PROGRESS
    })

    setProgress(progressCount)
  }

  const onPasswordInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    let password = e.currentTarget.value
    setFieldValue(props.name, password)
    props.setPassword(password)
    calculatePasswordPopoverContent(password)
    calculateProgress(password)
  }

  const id = props.id || props.name

  const passWordInput = (
    <PasswordInput {...field} {...props} onChange={onPasswordInputChange} className={`formik-field__span`} id={id} />
  )

  const input = props.popover ? (
    <Popover title="Password requirements" content={content} placement="right" className="password-popover" visible>
      {passWordInput}
    </Popover>
  ) : (
    passWordInput
  )

  const progressComponent = props.progress ? (
    <div className="progress-bar">
      <Progress percent={progressState} strokeColor="#11D175" showInfo={false} />
    </div>
  ) : null

  const errorComponent = showError ? <Error>{meta.error}</Error> : <Error></Error>

  return (
    <>
      <Label htmlFor={id} className=" no-select hand-on-hover">
        {props.label}
        {props.hint}
      </Label>
      {input}
      {progressComponent}
      {errorComponent}
    </>
  )
}
export { FormikInputField as InputPassword }
export default FormikInputField
